import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faHouse,
	faRulerVertical,
	faPersonSwimming,
	faWarehouse,
	faElevator,
	faSolarPanel,
	faRoad,
	faShieldHalved
} from '@fortawesome/free-solid-svg-icons';
import styles from './Bistritsa.module.css';
import tableStyles from './CompareLocation.module.css';
import Contact from '../ui/Contact.js';
import compareLocations from '../data/compareLocations.js';

import birdseye from '../assets/webp/birdseye.webp';
import air1 from '../assets/webp/Aerial_1_07.2.webp';

const shared = {
	en: {
		tableTitle: 'Side by side',
		criterion: 'Criterion',
		bistritsaCol: 'Bistritsa',
		whyTitle: 'Why Bistritsa wins',
		whyLink: 'Read how Bistritsa became Sofia\'s most expensive address',
		housesTitle: 'The houses on Izgled Street',
		housesIntro:
			'Vkushty is a boutique gated community of just four houses at the top of Izgled Street in Bistritsa — 640-780 sq.m each, on plots of 920-980 sq.m, at 897 m with a 360° panorama over Sofia.',
		facts: [
			{ icon: faHouse, text: 'Only 4 houses, 640-780 sq.m on three levels' },
			{ icon: faRulerVertical, text: '3.39 m clear ceiling height' },
			{ icon: faPersonSwimming, text: 'Heated pool in every house' },
			{ icon: faWarehouse, text: '4-car garage' },
			{ icon: faElevator, text: 'KONE elevator' },
			{ icon: faSolarPanel, text: 'Photovoltaics and heat pumps' },
			{ icon: faShieldHalved, text: 'Gated community with alarm system, cameras and perimeter sensors' },
			{ icon: faRoad, text: '5 minutes from the Sofia ring road' }
		],
		heroAlt: 'Panoramic view over Sofia from the Vkushty houses in Bistritsa',
		galleryAlt: 'The Vkushty gated community of houses in Bistritsa',
		bistritsaPageLink: 'See the full page about the houses in Bistritsa',
		ctaTitle: 'See the houses',
		ctaText: 'Request the blueprints and a 3D tour, watch the videos, or book an on-site viewing.',
		ctaDownload: 'Blueprints & 3D tour',
		ctaVideos: 'Videos',
		contactTitle: 'Book a viewing',
		contactText: 'Fill in the form and we will get back to you to arrange an on-site viewing in Bistritsa.'
	},
	bg: {
		tableTitle: 'Едно до друго',
		criterion: 'Критерий',
		bistritsaCol: 'Бистрица',
		whyTitle: 'Защо Бистрица печели',
		whyLink: 'Прочетете как Бистрица стана най-скъпият адрес в София',
		housesTitle: 'Къщите на ул. Изглед',
		housesIntro:
			'Вкъщи е бутиков затворен комплекс от само четири къщи на върха на ул. Изглед в Бистрица — по 640-780 кв.м, върху парцели от 920-980 кв.м, на 897 м с 360° панорама над София.',
		facts: [
			{ icon: faHouse, text: 'Само 4 къщи, 640-780 кв.м на три нива' },
			{ icon: faRulerVertical, text: 'Тавани 3,39 м светла височина' },
			{ icon: faPersonSwimming, text: 'Отопляем басейн във всяка къща' },
			{ icon: faWarehouse, text: 'Гараж за 4 коли' },
			{ icon: faElevator, text: 'Асансьор KONE' },
			{ icon: faSolarPanel, text: 'Фотоволтаици и термопомпи' },
			{ icon: faShieldHalved, text: 'Затворен комплекс със СОТ, камери и периметрови сензори' },
			{ icon: faRoad, text: '5 минути от Околовръстен път' }
		],
		heroAlt: 'Панорамна гледка над София от къщите Вкъщи в Бистрица',
		galleryAlt: 'Затвореният комплекс от къщи Вкъщи в Бистрица',
		bistritsaPageLink: 'Разгледайте пълната страница за къщите в Бистрица',
		ctaTitle: 'Разгледайте къщите',
		ctaText: 'Заявете чертежите и 3D обиколка, гледайте видеата или запишете оглед на място.',
		ctaDownload: 'Чертежи и 3D тур',
		ctaVideos: 'Видеа',
		contactTitle: 'Запишете оглед',
		contactText: 'Попълнете формата и ще се свържем с вас, за да уговорим оглед на място в Бистрица.'
	}
};

function CompareLocation({ district, languageEn }) {
	const location = compareLocations[district];
	const t = languageEn ? location.en : location.bg;
	const s = languageEn ? shared.en : shared.bg;

	const pagePath = `${languageEn ? '/en' : ''}/${location.slug}`;
	const blogPath = `${languageEn ? '/en' : ''}/blog/how-bistritsa-became-sofias-most-expensive-address`;
	const bistritsaPath = `${languageEn ? '/en' : ''}/kashti-bistritsa`;
	const downloadPath = `${languageEn ? '/en' : ''}/download`;
	const videosPath = `${languageEn ? '/en' : ''}/galeria`;

	return (
		<div className={styles.container}>
			<div id="snap-ready" style={{ display: 'none' }} />
			{/* defer={false} writes head tags synchronously — react-snap otherwise
			    races helmet's rAF-deferred update and can snapshot a bare <head>. */}
			<Helmet defer={false}>
				<html lang={languageEn ? 'en' : 'bg'} />
				<title>{t.title}</title>
				<meta name="description" content={t.metaDescription} />
				<link rel="canonical" href={`https://vkushty.com${pagePath}/`} />
				<meta property="og:title" content={t.title} />
				<meta property="og:description" content={t.metaDescription} />
				<meta property="og:url" content={`https://vkushty.com${pagePath}/`} />
				<meta property="og:type" content="website" />
				<meta property="og:image" content="https://vkushty.com/og-image.jpg" />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:image" content="https://vkushty.com/og-image.jpg" />
				<link rel="alternate" hreflang="bg" href={`https://vkushty.com/${location.slug}/`} />
				<link rel="alternate" hreflang="en" href={`https://vkushty.com/en/${location.slug}/`} />
				<link rel="alternate" hreflang="x-default" href={`https://vkushty.com/${location.slug}/`} />
				<meta property="og:locale" content={languageEn ? "en_US" : "bg_BG"} />
				<meta property="og:locale:alternate" content={languageEn ? "bg_BG" : "en_US"} />
				<script type="application/ld+json">
					{JSON.stringify({
						'@context': 'https://schema.org',
						'@type': 'WebPage',
						name: t.title,
						description: t.metaDescription,
						url: `https://vkushty.com${pagePath}/`,
						image: 'https://vkushty.com/og-image.jpg'
					})}
				</script>
			</Helmet>

			<h1>{t.h1}</h1>
			<p className={styles.subtitle}>{t.subtitle}</p>

			<div className={styles.introSection}>
				<p>{t.intro}</p>
			</div>

			<img className={styles.heroImage} src={birdseye} alt={s.heroAlt} />

			<h2>{s.tableTitle}</h2>
			<div className={tableStyles.tableWrapper}>
				<table className={tableStyles.compareTable}>
					<thead>
						<tr>
							<th>{s.criterion}</th>
							<th>{t.name}</th>
							<th>{s.bistritsaCol}</th>
						</tr>
					</thead>
					<tbody>
						{t.rows.map((row, index) => (
							<tr key={index}>
								<td className={tableStyles.criterion}>{row.label}</td>
								<td>{row.other}</td>
								<td className={tableStyles.bistritsaCell}>{row.bistritsa}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<h2>{s.whyTitle}</h2>
			{t.whyParagraphs.map((p, index) => (
				<p key={index} className={styles.sectionText}>
					{p}
				</p>
			))}
			<p className={styles.sectionText}>
				<Link className={styles.inlineLink} to={blogPath}>
					{s.whyLink}
				</Link>
			</p>

			<h2>{s.housesTitle}</h2>
			<p className={styles.sectionText}>{s.housesIntro}</p>
			<div className={styles.featureGrid}>
				{s.facts.map((fact, index) => (
					<div key={index} className={styles.featureItem}>
						<div className={styles.featureIcon}>
							<FontAwesomeIcon icon={fact.icon} />
						</div>
						<p>{fact.text}</p>
					</div>
				))}
			</div>
			<p className={styles.sectionText}>
				<Link className={styles.inlineLink} to={bistritsaPath}>
					{s.bistritsaPageLink}
				</Link>
			</p>

			<img className={styles.heroImage} src={air1} alt={s.galleryAlt} loading="lazy" />

			<h2>{s.ctaTitle}</h2>
			<p className={styles.sectionText}>{s.ctaText}</p>
			<div className={styles.ctaRow}>
				<Link className={styles.ctaButton} to={downloadPath}>
					{s.ctaDownload}
				</Link>
				<Link className={styles.ctaButtonSecondary} to={videosPath}>
					{s.ctaVideos}
				</Link>
			</div>

			<h2>{s.contactTitle}</h2>
			<p className={styles.sectionText}>{s.contactText}</p>
			<div className={styles.contactWrapper}>
				<Contact languageEn={languageEn} />
			</div>
		</div>
	);
}

export default CompareLocation;
